import React, { useCallback, useEffect, useMemo, useState } from 'react';

const envOrigin = (import.meta.env.VITE_API_ORIGIN || '').trim().replace(/\/$/, '');
const isPages = typeof window !== 'undefined' && /\.pages\.dev$/i.test(window.location?.hostname || '');
const API_BASE = isPages ? '' : (envOrigin || '');
const api = (path) => (API_BASE ? API_BASE + (path.startsWith('/') ? path : `/${path}`) : path);

const STORAGE_ACCOUNTS = 'kzz_accounts_local';
const STORAGE_RECORDS = 'kzz_records_local';

function uid(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function readLocalJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeLocalJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {}
}

function formatWinRate(pct, estimated) {
  if (pct == null || !Number.isFinite(Number(pct))) return '待公布';
  const n = Number(pct);
  const text = `${n.toFixed(4)}%`;
  return estimated ? `约 ${text}` : text;
}

function formatYi(v) {
  if (v == null || !Number.isFinite(Number(v))) return '-';
  return `${Number(v).toFixed(2)} 亿`;
}

function formatMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '¥0.00';
  const sign = n > 0 ? '+' : '';
  return `${sign}¥${n.toFixed(2)}`;
}

function statusTone(status) {
  if (status === 'today') return 'hot';
  if (status === 'upcoming' || status === 'pipeline') return 'warm';
  if (status === 'pending_list' || status === 'listing_today') return 'cool';
  return 'muted';
}

function BondTable({ rows, onFill }) {
  if (!rows.length) {
    return <div className="global-info-empty">暂无此类转债。</div>;
  }
  return (
    <div className="kzz-table-wrap">
      <table className="kzz-table">
        <thead>
          <tr>
            <th>状态</th>
            <th>转债</th>
            <th>正股</th>
            <th>申购日</th>
            <th>上市日</th>
            <th>规模</th>
            <th>评级</th>
            <th>中签率</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((it) => (
            <tr key={it.id || `${it.bondCode}-${it.stockCode}-${it.bondName}`} className={it.status === 'today' ? 'is-today' : ''}>
              <td>
                <em className={`summary-badge score-${statusTone(it.status)}`}>{it.statusLabel}</em>
              </td>
              <td>
                <strong>{it.bondName}</strong>
                <div className="kzz-sub">
                  {[it.bondCode, it.applyCode ? `申购 ${it.applyCode}` : '', it.progress && it.status === 'pipeline' ? it.progress : '']
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </td>
              <td>
                {it.stockName || '-'}
                <div className="kzz-sub">{it.stockCode || ''}</div>
              </td>
              <td>{it.applyDate || '待公告'}</td>
              <td>{it.listingDate || '待定'}</td>
              <td>{formatYi(it.issueScaleYi)}</td>
              <td>{it.rating || '-'}</td>
              <td>
                <span className={it.winRateEstimated ? 'kzz-rate-est' : 'kzz-rate'}>
                  {formatWinRate(it.winRateDisplayPct, it.winRateEstimated)}
                </span>
              </td>
              <td>
                <button type="button" className="global-info-btn soft" onClick={() => onFill(it)}>
                  记入统计
                </button>
                {it.detailUrl ? (
                  <a className="kzz-link" href={it.detailUrl} target="_blank" rel="noreferrer">
                    详情
                  </a>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ConvertibleBondPage() {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState('');
  const [board, setBoard] = useState(null);
  const [accounts, setAccounts] = useState(() => readLocalJson(STORAGE_ACCOUNTS, []));
  const [records, setRecords] = useState(() => readLocalJson(STORAGE_RECORDS, []));
  const [accountName, setAccountName] = useState('');
  const [accountNote, setAccountNote] = useState('');
  const [upcomingOpen, setUpcomingOpen] = useState(false);
  const [form, setForm] = useState({
    accountId: '',
    bondCode: '',
    bondName: '',
    applyCode: '',
    applyDate: '',
    lots: '1',
    profit: '',
    note: '',
  });

  const runLabeled = async (label, fn) => {
    setLoading(label);
    setMessage('');
    try {
      await fn();
    } catch (err) {
      setMessage(err?.message || '请求失败');
    } finally {
      setLoading('');
    }
  };

  const persistLedgerLocal = useCallback((nextAccounts, nextRecords) => {
    writeLocalJson(STORAGE_ACCOUNTS, nextAccounts);
    writeLocalJson(STORAGE_RECORDS, nextRecords);
  }, []);

  const syncLedgerToServer = useCallback(async (nextAccounts, nextRecords) => {
    const resp = await fetch(api('/api/convertible-bond/ledger'), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accounts: nextAccounts, records: nextRecords }),
    });
    const body = await resp.json().catch(() => ({}));
    if (!resp.ok || body.success === false) {
      throw new Error(body?.message || `同步失败 ${resp.status}`);
    }
    return body.data;
  }, []);

  const loadUpcoming = useCallback(async () => {
    await runLabeled('可转债列表', async () => {
      const resp = await fetch(api('/api/convertible-bond/upcoming'), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) {
        throw new Error(body?.message || `拉取失败 ${resp.status}`);
      }
      setBoard(body.data || null);
      setMessage(`已更新可转债列表 ${body.data?.items?.length || 0} 只`);
    });
  }, []);

  const loadLedger = useCallback(async () => {
    try {
      const resp = await fetch(api('/api/convertible-bond/ledger'), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) return;
      const data = body.data || {};
      const serverAccounts = Array.isArray(data.accounts) ? data.accounts : [];
      const serverRecords = Array.isArray(data.records) ? data.records : [];
      // 服务端有数据优先；否则保留本机草稿并尝试上传
      if (serverAccounts.length || serverRecords.length) {
        setAccounts(serverAccounts);
        setRecords(serverRecords);
        persistLedgerLocal(serverAccounts, serverRecords);
      } else if (accounts.length || records.length) {
        await syncLedgerToServer(accounts, records);
      }
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadUpcoming();
    loadLedger();
  }, [loadUpcoming, loadLedger]);

  useEffect(() => {
    if (!form.accountId && accounts[0]?.id) {
      setForm((f) => ({ ...f, accountId: accounts[0].id }));
    }
  }, [accounts, form.accountId]);

  const items = board?.items || [];
  const upcomingItems = useMemo(
    () => items.filter((it) => ['today', 'upcoming', 'pipeline'].includes(it.status)),
    [items],
  );
  const recentItems = useMemo(
    () => items.filter((it) => !['today', 'upcoming', 'pipeline'].includes(it.status)),
    [items],
  );

  const summary = useMemo(() => {
    const lots = records.reduce((s, r) => s + (Number(r.lots) || 0), 0);
    const profit = records.reduce((s, r) => s + (Number(r.profit) || 0), 0);
    const byAccount = accounts.map((acc) => {
      const rows = records.filter((r) => r.accountId === acc.id);
      return {
        ...acc,
        lots: rows.reduce((s, r) => s + (Number(r.lots) || 0), 0),
        profit: rows.reduce((s, r) => s + (Number(r.profit) || 0), 0),
        count: rows.length,
      };
    });
    return { lots, profit, byAccount, recordCount: records.length };
  }, [accounts, records]);

  const addAccount = async () => {
    const name = accountName.trim();
    if (!name) {
      setMessage('请填写账户名称');
      return;
    }
    const next = [...accounts, { id: uid('acc'), name, note: accountNote.trim() }];
    setAccounts(next);
    persistLedgerLocal(next, records);
    setAccountName('');
    setAccountNote('');
    await runLabeled('保存账户', async () => {
      await syncLedgerToServer(next, records);
      setMessage(`已添加账户「${name}」`);
    });
  };

  const removeAccount = async (id) => {
    const nextAccounts = accounts.filter((a) => a.id !== id);
    const nextRecords = records.filter((r) => r.accountId !== id);
    setAccounts(nextAccounts);
    setRecords(nextRecords);
    persistLedgerLocal(nextAccounts, nextRecords);
    await runLabeled('删除账户', async () => {
      await syncLedgerToServer(nextAccounts, nextRecords);
      setMessage('已删除账户及其中签记录');
    });
  };

  const fillFromBond = (bond) => {
    setForm((f) => ({
      ...f,
      bondCode: bond.bondCode || '',
      bondName: bond.bondName || '',
      applyCode: bond.applyCode || '',
      applyDate: bond.applyDate || '',
    }));
    setMessage(`已填入 ${bond.bondName || bond.bondCode}，请补中签数与收益后保存`);
  };

  const addRecord = async () => {
    const acc = accounts.find((a) => a.id === form.accountId);
    if (!acc) {
      setMessage('请先添加并选择账户');
      return;
    }
    if (!form.bondName.trim() && !form.bondCode.trim()) {
      setMessage('请填写转债名称或代码');
      return;
    }
    const lots = Number(form.lots);
    if (!Number.isFinite(lots) || lots <= 0) {
      setMessage('中签数需大于 0（通常 1 签 = 10 张 = 1000 元面值）');
      return;
    }
    const rec = {
      id: uid('rec'),
      accountId: acc.id,
      accountName: acc.name,
      bondCode: form.bondCode.trim(),
      bondName: form.bondName.trim(),
      applyCode: form.applyCode.trim(),
      applyDate: form.applyDate.trim(),
      lots,
      profit: Number(form.profit) || 0,
      note: form.note.trim(),
      createdAt: Date.now(),
    };
    const next = [rec, ...records];
    setRecords(next);
    persistLedgerLocal(accounts, next);
    setForm((f) => ({ ...f, lots: '1', profit: '', note: '' }));
    await runLabeled('保存中签', async () => {
      await syncLedgerToServer(accounts, next);
      setMessage(`已记录 ${acc.name} 中 ${rec.bondName || rec.bondCode} ${lots} 签`);
    });
  };

  const removeRecord = async (id) => {
    const next = records.filter((r) => r.id !== id);
    setRecords(next);
    persistLedgerLocal(accounts, next);
    await runLabeled('删除记录', async () => {
      await syncLedgerToServer(accounts, next);
      setMessage('已删除该条中签记录');
    });
  };

  return (
    <div className="global-info-page kzz-page">
      <section className="global-info-hero">
        <div>
          <span className="global-info-eyebrow">Convertible Bond IPO</span>
          <h1>可转债申购</h1>
          <p>
            汇总即将申购 / 待发转债与近期已申购数据（集思录 + 东方财富），并支持多账户登记中签与收益。
            1 签通常对应 10 张（面值约 1000 元）。
          </p>
        </div>
        <div className="global-info-token-card">
          <div className="global-info-token-note">
            待发进度来自集思录；已申购与中签率来自东方财富。未公布中签率时用近 20 只中位数估算。
          </div>
          <div className="global-info-token-actions">
            <button type="button" className="global-info-btn" disabled={!!loading} onClick={loadUpcoming}>
              刷新申购列表
            </button>
            <button type="button" className="global-info-btn soft" disabled={!!loading} onClick={loadLedger}>
              同步统计
            </button>
          </div>
          <small>
            {board?.today ? `上海日期 ${board.today}` : '尚未加载'}
            {board?.upcomingCount != null ? ` · 已定申购日 ${board.upcomingCount}` : ''}
            {board?.pipelineCount != null ? ` · 待发 ${board.pipelineCount}` : ''}
            {board?.estimatedWinRatePct != null
              ? ` · 中签率中位约 ${Number(board.estimatedWinRatePct).toFixed(4)}%`
              : ''}
          </small>
        </div>
      </section>

      {(message || loading) && (
        <div className={`global-info-message ${loading ? 'is-loading' : ''}`}>
          {loading ? `正在加载：${loading}…` : message}
        </div>
      )}

      <section className={`global-info-panel kzz-collapsible ${upcomingOpen ? 'is-open' : 'is-collapsed'}`}>
        <div className="global-info-panel-head">
          <div>
            <h2>即将申购 / 待发</h2>
            <p>
              {board?.note
                || '优先展示已公告申购日的转债；若暂无，则展示集思录待发进度（同意注册、上市委通过等）。'}
              {!upcomingOpen ? ` · 共 ${upcomingItems.length} 只（已折叠）` : ''}
            </p>
          </div>
          <button
            type="button"
            className="global-info-btn soft"
            onClick={() => setUpcomingOpen((v) => !v)}
            aria-expanded={upcomingOpen}
          >
            {upcomingOpen ? '收起' : '展开'}
          </button>
        </div>
        {upcomingOpen && <BondTable rows={upcomingItems} onFill={fillFromBond} />}
      </section>

      <section className="global-info-panel">
        <div className="global-info-panel-head">
          <div>
            <h2>近期已申购 / 待上市</h2>
            <p>近 30 天已完成网上申购，或已确定上市日的转债，便于对照中签率与记收益。</p>
          </div>
        </div>
        <BondTable rows={recentItems} onFill={fillFromBond} />
      </section>

      <section className="global-info-panel">
        <div className="global-info-panel-head">
          <div>
            <h2>账户中签统计</h2>
            <p>多账户登记：中几签、中哪只转债、收益多少。数据同步到服务端，并缓存在本机。</p>
          </div>
        </div>

        <div className="kzz-stats-grid">
          <div className="kzz-stat-card">
            <span>账户数</span>
            <strong>{accounts.length}</strong>
          </div>
          <div className="kzz-stat-card">
            <span>中签记录</span>
            <strong>{summary.recordCount}</strong>
          </div>
          <div className="kzz-stat-card">
            <span>累计中签</span>
            <strong>{summary.lots} 签</strong>
          </div>
          <div className="kzz-stat-card">
            <span>累计收益</span>
            <strong className={summary.profit >= 0 ? 'is-up' : 'is-down'}>{formatMoney(summary.profit)}</strong>
          </div>
        </div>

        <div className="global-info-filters kzz-account-form">
          <label>
            新账户名称
            <input value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder="如：华泰主号 / 招商副号" />
          </label>
          <label>
            备注
            <input value={accountNote} onChange={(e) => setAccountNote(e.target.value)} placeholder="可选" />
          </label>
          <div className="global-info-actions">
            <button type="button" className="global-info-btn" disabled={!!loading} onClick={addAccount}>
              添加账户
            </button>
          </div>
        </div>

        {summary.byAccount.length > 0 && (
          <div className="kzz-account-chips">
            {summary.byAccount.map((acc) => (
              <div key={acc.id} className="kzz-account-chip">
                <div>
                  <strong>{acc.name}</strong>
                  <span>
                    {acc.count} 笔 · {acc.lots} 签 · {formatMoney(acc.profit)}
                  </span>
                  {acc.note ? <em>{acc.note}</em> : null}
                </div>
                <button type="button" className="global-info-btn ghost" onClick={() => removeAccount(acc.id)}>
                  删除
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="global-info-filters kzz-record-form">
          <label>
            账户
            <select value={form.accountId} onChange={(e) => setForm((f) => ({ ...f, accountId: e.target.value }))}>
              <option value="">请选择</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </label>
          <label>
            转债名称
            <input value={form.bondName} onChange={(e) => setForm((f) => ({ ...f, bondName: e.target.value }))} placeholder="如：华科转债" />
          </label>
          <label>
            转债代码
            <input value={form.bondCode} onChange={(e) => setForm((f) => ({ ...f, bondCode: e.target.value }))} placeholder="123281" />
          </label>
          <label>
            申购代码
            <input value={form.applyCode} onChange={(e) => setForm((f) => ({ ...f, applyCode: e.target.value }))} placeholder="可选" />
          </label>
          <label>
            申购日
            <input value={form.applyDate} onChange={(e) => setForm((f) => ({ ...f, applyDate: e.target.value }))} placeholder="2026-08-06" />
          </label>
          <label>
            中签数（签）
            <input type="number" min={0.1} step={1} value={form.lots} onChange={(e) => setForm((f) => ({ ...f, lots: e.target.value }))} />
          </label>
          <label>
            收益（元）
            <input type="number" step={0.01} value={form.profit} onChange={(e) => setForm((f) => ({ ...f, profit: e.target.value }))} placeholder="卖出后填写，可为负" />
          </label>
          <label>
            备注
            <input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="可选" />
          </label>
        </div>
        <div className="global-info-actions">
          <button type="button" className="global-info-btn" disabled={!!loading || !accounts.length} onClick={addRecord}>
            添加中签记录
          </button>
        </div>

        {records.length === 0 ? (
          <div className="global-info-empty" style={{ marginTop: 16 }}>暂无中签记录。可先添加账户，再从上方列表点「记入统计」。</div>
        ) : (
          <div className="kzz-table-wrap" style={{ marginTop: 16 }}>
            <table className="kzz-table">
              <thead>
                <tr>
                  <th>账户</th>
                  <th>转债</th>
                  <th>申购日</th>
                  <th>中签</th>
                  <th>收益</th>
                  <th>备注</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id}>
                    <td>{r.accountName || '-'}</td>
                    <td>
                      <strong>{r.bondName || '-'}</strong>
                      <div className="kzz-sub">
                        {[r.bondCode, r.applyCode ? `申购 ${r.applyCode}` : ''].filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td>{r.applyDate || '-'}</td>
                    <td>{r.lots} 签</td>
                    <td className={Number(r.profit) >= 0 ? 'is-up' : 'is-down'}>{formatMoney(r.profit)}</td>
                    <td>{r.note || '-'}</td>
                    <td>
                      <button type="button" className="global-info-btn ghost" onClick={() => removeRecord(r.id)}>
                        删除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
